import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import {
    AccordionPanel,
    Field,
    makeStyles,
    shorthands,
    tokens,
    Toolbar,
    ToolbarRadioButton,
    ToolbarRadioGroup,
    Tooltip,
} from '@fluentui/react-components'
import { Stack, StackItem } from '@fluentui/react'
import { PricePrecedence } from '../../../../../../config/finance/financeConfig'
import MoneyInput from '../../../../../Input/MoneyInput'
import VatTypeCombobox from '../../../../../Combobox/i18n/VatTypeCombobox'
import { useFormikContext } from 'formik'
import { InvoiceMutationDto } from '../../../../../../config/finance/invoiceConfig'
import { LineItemGroupDialogProps } from './LineItemGroupDialog'
import { PriceVat } from '../../InvoiceForm'
import { formatPriceVat } from '../../../../../../helpers/priceHelper'
import { formatCurrencyDecimals } from '../../../../../../helpers/numberHelper'
import useCurrency from '../../../../../../hooks/useCurrency'
import { Alert } from '@fluentui/react-components/unstable'
import { CommitEventType } from '../../../../../Input/FormattableNumberInput'
import useVatTypes from '../../../../../../hooks/useVatTypes'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    toolbar: {
        ...shorthands.padding(0),
    },
    toolbarGroup: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    autoWidth: {
        width: 'inherit',
        minWidth: 'inherit',
    },
})

const PriceKeyMap = new Map<PricePrecedence, 'subtotal' | 'total'>([
    [PricePrecedence.Subtotal, 'subtotal'],
    [PricePrecedence.Total, 'total'],
])

export default function RatePanel(props: LineItemGroupDialogProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { values } = useFormikContext<InvoiceMutationDto>()
    const [price, setPrice] = useState<PriceVat>()
    const { getVatType } = useVatTypes()
    const subtotalRef = useRef<HTMLInputElement>()
    const totalRef = useRef<HTMLInputElement>()
    const { currency } = useCurrency(values.currency)

    const precedence = props.values.precedence
    const isFixedFee = precedence !== PricePrecedence.Inherited

    const updatePrice = (precedence: PricePrecedence.Subtotal | PricePrecedence.Total) => {
        if (price && precedence === props.values.precedence) {
            const newPrice = formatPriceVat(
                price[PriceKeyMap.get(precedence)],
                getVatType(price?.vat?.[0]?.type || props.values.vatType || props.defaultVatType),
                currency,
                precedence,
            )
            if (JSON.stringify(price) !== JSON.stringify(newPrice)) {
                setPrice(newPrice)
            }
        }
    }

    useEffect(() => {
        // Set the initial price when we select a different calculation method.
        if (isFixedFee) {
            setPrice(
                props.lineItemEntities.getGroupPrice(
                    props.values,
                    getVatType(price?.vat?.[0]?.type || props.values.vatType || props.defaultVatType),
                    [undefined, null].includes(props.values.price),
                ),
            )

            setTimeout(() => {
                const RefMap = new Map([
                    [PricePrecedence.Subtotal, subtotalRef],
                    [PricePrecedence.Total, totalRef],
                ])

                RefMap.get(precedence)?.current?.blur()
                RefMap.get(precedence)?.current?.focus()
            }, 100)
        } else {
            setPrice(undefined)
        }
    }, [precedence])

    useEffect(() => {
        updatePrice(PricePrecedence.Subtotal)
    }, [price?.subtotal, price?.vat])

    useEffect(() => {
        updatePrice(PricePrecedence.Total)
    }, [price?.total, price?.vat])

    useEffect(() => {
        const newPrice =
            isFixedFee && price
                ? formatCurrencyDecimals(price[PriceKeyMap.get(precedence)], currency, 'en', 'en', false)
                : undefined

        props.setValues({
            ...props.values,
            price: newPrice,
            vatType: isFixedFee && price ? price?.vat?.[0]?.type : undefined,
        })
    }, [price, precedence])

    return (
        <AccordionPanel>
            <Stack tokens={{ childrenGap: 's1' }}>
                <Toolbar
                    className={styles.toolbar}
                    checkedValues={{ ['precedence']: [precedence.valueOf()] }}
                    onCheckedValueChange={(_, data) =>
                        props.setProperty('precedence', data.checkedItems?.[0] || PricePrecedence.Inherited)
                    }
                >
                    <ToolbarRadioGroup className={styles.toolbarGroup}>
                        <Tooltip
                            relationship="description"
                            content={translate('invoice.edit.form.invoicegroup.rate.inherited.tooltip')}
                            withArrow
                        >
                            <ToolbarRadioButton name="precedence" value={PricePrecedence.Inherited}>
                                {translate('invoice.edit.form.invoicegroup.rate.inherited')}
                            </ToolbarRadioButton>
                        </Tooltip>
                        <Tooltip
                            relationship="description"
                            content={translate('invoice.edit.form.invoicegroup.rate.subtotal.tooltip')}
                            withArrow
                        >
                            <ToolbarRadioButton name="precedence" value={PricePrecedence.Subtotal}>
                                {translate('invoice.edit.form.invoicegroup.rate.subtotal')}
                            </ToolbarRadioButton>
                        </Tooltip>
                        <Tooltip
                            relationship="description"
                            content={translate('invoice.edit.form.invoicegroup.rate.total.tooltip')}
                            withArrow
                        >
                            <ToolbarRadioButton name="precedence" value={PricePrecedence.Total}>
                                {translate('invoice.edit.form.invoicegroup.rate.total')}
                            </ToolbarRadioButton>
                        </Tooltip>
                    </ToolbarRadioGroup>
                </Toolbar>
                {isFixedFee && price && (
                    <Stack horizontal tokens={{ childrenGap: 'm' }} grow>
                        <StackItem shrink grow>
                            <Field label={translate('invoice.edit.form.invoicegroup.rate.subtotal.label')}>
                                <MoneyInput
                                    allowNegative
                                    currency={values.currency}
                                    onCommit={(eventType: CommitEventType, value: string) => {
                                        if (
                                            eventType === CommitEventType.OnLoad &&
                                            precedence === PricePrecedence.Subtotal
                                        ) {
                                            subtotalRef.current?.blur()
                                            subtotalRef.current?.focus()
                                        }
                                        if (precedence === PricePrecedence.Subtotal && price) {
                                            setPrice({ ...price, subtotal: Number(value) })
                                        }
                                    }}
                                    value={price?.subtotal}
                                    disabled={precedence !== PricePrecedence.Subtotal}
                                    ref={subtotalRef}
                                />
                            </Field>
                        </StackItem>
                        <StackItem shrink grow>
                            <Field label={translate('invoice.edit.form.invoicegroup.rate.vat.label')} required>
                                <VatTypeCombobox
                                    selectedOptions={(price?.vat || []).map(vat => vat.type) || []}
                                    onOptionSelect={(_, { selectedOptions }) =>
                                        setPrice({
                                            ...price,
                                            vat: selectedOptions.map(vatType => ({ type: vatType, value: 0 })),
                                        })
                                    }
                                    className={styles.autoWidth}
                                />
                            </Field>
                        </StackItem>
                        <StackItem shrink grow>
                            <Field label={translate('invoice.edit.form.invoicegroup.rate.total.label')}>
                                <MoneyInput
                                    allowNegative
                                    currency={values.currency}
                                    onCommit={(eventType: CommitEventType, value: string) => {
                                        if (
                                            eventType === CommitEventType.OnLoad &&
                                            precedence === PricePrecedence.Total
                                        ) {
                                            totalRef.current?.blur()
                                            totalRef.current?.focus()
                                        }
                                        if (precedence === PricePrecedence.Total && price) {
                                            setPrice({ ...price, total: Number(value) })
                                        }
                                    }}
                                    value={price?.total}
                                    disabled={precedence !== PricePrecedence.Total}
                                    ref={totalRef}
                                />
                            </Field>
                        </StackItem>
                    </Stack>
                )}
                {isFixedFee && props.values.lineItems.length > 0 && (
                    <Alert intent="info">{translate('invoice.edit.form.invoicegroup.rate.alert')}</Alert>
                )}
            </Stack>
        </AccordionPanel>
    )
}
